/**
 * Storage utilities with quota management and graceful fallback
 */

class StorageManager {
  constructor() {
    this.memoryStorage = new Map();
    this.isLocalStorageAvailable = this.checkLocalStorageAvailability();
  }

  checkLocalStorageAvailability() {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      console.warn('localStorage not available, using memory storage fallback');
      return false;
    }
  }

  setItem(key, value, options = {}) {
    const { 
      compress = false, 
      maxAge = null, 
      priority = 'normal' // 'high', 'normal', 'low'
    } = options;

    try {
      let dataToStore = value;
      
      // Add metadata
      const storageItem = {
        data: dataToStore,
        timestamp: Date.now(),
        maxAge,
        priority,
        size: JSON.stringify(dataToStore).length
      };

      const serialized = JSON.stringify(storageItem);

      if (this.isLocalStorageAvailable) {
        try {
          localStorage.setItem(key, serialized);
          return true;
        } catch (e) {
          if (e.name === 'QuotaExceededError' || e.code === 22) {
            console.warn('localStorage quota exceeded, attempting cleanup');
            
            // Try to free up space
            if (this.cleanupStorage()) {
              try {
                localStorage.setItem(key, serialized);
                return true;
              } catch (retryError) {
                console.warn('Still no space after cleanup, using memory storage');
              }
            }
          }
          
          // Fallback to memory storage
          this.memoryStorage.set(key, storageItem);
          return true;
        }
      } else {
        // Use memory storage
        this.memoryStorage.set(key, storageItem);
        return true;
      }
    } catch (error) {
      console.error('Error storing data:', error);
      return false;
    }
  }

  getItem(key) {
    try {
      let storageItem = null;

      // Try localStorage first
      if (this.isLocalStorageAvailable) {
        const stored = localStorage.getItem(key);
        if (stored) {
          try {
            storageItem = JSON.parse(stored);
          } catch (e) {
            console.warn('Failed to parse stored data for key:', key);
            localStorage.removeItem(key);
            return null;
          }
        }
      }

      // Fallback to memory storage
      if (!storageItem && this.memoryStorage.has(key)) {
        storageItem = this.memoryStorage.get(key);
      }

      if (!storageItem) {
        return null;
      }

      // Check if item has expired
      if (storageItem.maxAge && Date.now() - storageItem.timestamp > storageItem.maxAge) {
        this.removeItem(key);
        return null;
      }

      return storageItem.data;
    } catch (error) {
      console.error('Error retrieving data:', error);
      return null;
    }
  }

  removeItem(key) {
    try {
      if (this.isLocalStorageAvailable) {
        localStorage.removeItem(key);
      }
      this.memoryStorage.delete(key);
      return true;
    } catch (error) {
      console.error('Error removing data:', error);
      return false;
    }
  }

  clear() {
    try {
      if (this.isLocalStorageAvailable) {
        localStorage.clear();
      }
      this.memoryStorage.clear();
      return true;
    } catch (error) {
      console.error('Error clearing storage:', error);
      return false;
    }
  }

  cleanupStorage() {
    if (!this.isLocalStorageAvailable) {
      return false;
    }

    try {
      const items = [];
      
      // Collect all items with metadata
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        const value = localStorage.getItem(key);
        
        try {
          const parsed = JSON.parse(value);
          if (parsed && typeof parsed === 'object' && parsed.timestamp) {
            items.push({
              key,
              ...parsed,
              age: Date.now() - parsed.timestamp
            });
          }
        } catch (e) {
          // Invalid format, mark for deletion
          items.push({
            key,
            priority: 'low',
            age: Infinity,
            size: value.length
          });
        }
      }

      // Sort by priority and age (oldest first)
      items.sort((a, b) => {
        const priorityOrder = { low: 0, normal: 1, high: 2 };
        const aPriority = priorityOrder[a.priority] || 1;
        const bPriority = priorityOrder[b.priority] || 1;
        
        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }
        
        return b.age - a.age;
      });

      // Remove expired items first
      let removedCount = 0;
      for (const item of items) {
        if (item.maxAge && item.age > item.maxAge) {
          localStorage.removeItem(item.key);
          removedCount++;
        }
      }

      // If still need space, remove low priority items
      if (removedCount < 5) {
        for (const item of items) {
          if (item.priority === 'low' && removedCount < 10) {
            localStorage.removeItem(item.key);
            removedCount++;
          }
        }
      }

      console.log(`Cleaned up ${removedCount} storage items`);
      return removedCount > 0;
    } catch (error) {
      console.error('Error during storage cleanup:', error);
      return false;
    }
  }

  getStorageInfo() {
    const info = {
      isLocalStorageAvailable: this.isLocalStorageAvailable,
      memoryStorageSize: this.memoryStorage.size,
      localStorageSize: 0,
      estimatedUsage: 0
    };

    if (this.isLocalStorageAvailable) {
      try {
        let totalSize = 0;
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          const value = localStorage.getItem(key);
          totalSize += key.length + value.length;
        }
        info.localStorageSize = localStorage.length;
        info.estimatedUsage = totalSize;
      } catch (e) {
        console.warn('Could not calculate storage usage');
      }
    }

    return info;
  }
}

// Create singleton instance
const storageManager = new StorageManager();

// Export convenience methods
export const setStorageItem = (key, value, options) => storageManager.setItem(key, value, options);
export const getStorageItem = (key) => storageManager.getItem(key);
export const removeStorageItem = (key) => storageManager.removeItem(key);
export const clearStorage = () => storageManager.clear();
export const cleanupStorage = () => storageManager.cleanupStorage();
export const getStorageInfo = () => storageManager.getStorageInfo();

export default storageManager;